import app from 'flarum/forum/app';

/**
 * Scheduler API helper (v0.17.57 起读课表 /schedule)
 *
 * 封装 m.request, 走 /api/ziiven/scheduler/* 路由
 * 通用 list / create / update / delete / patch
 * + 校区课表 GET /schools/{id}/schedule
 */
export default class Api {
  /**
   * v0.19.00 — `opts.errorHandler`: **可选**的自定义错误处理器 (静默轮询用)。
   *
   * ⚠️ **为什么需要它**: Flarum 2.0 的 `app.request` 在请求失败时, **默认会弹一个全局错误
   *   Alert** (core `Application.tsx` 的 `requestErrorCatch` → `requestErrorDefaultHandler`)。
   *   ⇒ 「静默轮询」如果直接用默认行为, 一次网络抖动就会在用户屏幕上弹一条红条 ——
   *   那就违背了「轮询失败必须完全静默」这条口径。
   *   ⇒ core 的约定 (见 `FlarumRequestOptions.errorHandler` 的注释):
   *     **返回 `false` 才会回落到默认处理器** ⇒ 想要「安静」就**不要返回 false**。
   *
   * ⛔ 不传 `opts` / `opts.errorHandler` 为 undefined 时, 行为与改前**逐字相同**
   *   (所有既有调用方零影响)。
   */
  static list(resource, params, opts) {
    if (params === void 0) {
      params = {};
    }
    if (opts === void 0) {
      opts = {};
    }
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource,
      params,
      errorHandler: opts.errorHandler
    }).then(r => r || []);
  }
  static get(resource, params) {
    if (params === void 0) {
      params = {};
    }
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource,
      params
    });
  }

  /**
   * v0.19.30 — `create` 新增**可选**第三参 `opts` (**向后兼容的纯增量**)。
   *
   * ⚠️ **用途**: 调用方可以传 `opts.errorHandler` 来**抑制 Flarum 2.0 默认的全局红条**
   *   (core `Application.tsx` 的 `requestErrorCatch` ⇒ 默认会弹一条全局红色 Alert)
   *   ⇒ 供「就地错误条」接管失败态, 避免页面同时出现两条错误提示。
   *   ⚠️ core 契约: errorHandler 返回 `undefined` ⇒ **不弹**全局红条;
   *     返回 `false` ⇒ **回落默认** ⇒ **会弹** ⇒ ⛔ 绝不能返回 false。
   *
   * ⚛ ⛔ 不传 `opts` 时行为与改前**逐字相同** ⇒ 其它 30+ 调用方**零影响**。
   * ⚠️ `...opts` 放**最后** ⇒ 调用方**可以**覆盖 url / body / serialize / params。
   * ⚠️ ⇒ 约定：**只允许传 `errorHandler`** (本仓唯一用途); 将来若有别的键,
   * ⚠️    建议改成**显式白名单取值** (把 errorHandler 单独拎出来传), 别让 `...opts` 裸奔。
   */
  static create(resource, attributes, opts) {
    if (opts === void 0) {
      opts = {};
    }
    // Flarum 2.0 mithril XHR: serialize 是 body transformer, mithril 会调它.
    // 直接传 string body 会被 JSON.stringify 再次包装 (double-encoded), 必须用 object + serialize 显式控制
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource,
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      },
      ...opts
    });
  }
  static update(resource, id, attributes) {
    const url = app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource + '/' + id;
    // v0.8.4 — PATCH 跟 DELETE 一样被 app.request 改成 POST (辉哥 10/6 拍板踩坑,
    //   见 /tmp/.../check_patch2.js: app.request({method:'PATCH'}) → wire 上是 POST → 405)
    //   走 window.m.request 兜底, 跟 Api.delete 同款处理
    const mithril = typeof window !== 'undefined' ? window.m : null;
    if (mithril && typeof mithril.request === 'function') {
      return mithril.request({
        method: 'PATCH',
        url,
        body: {
          data: {
            attributes
          }
        },
        serialize: x => JSON.stringify(x),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': app.session.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
    }
    return app.request({
      method: 'PATCH',
      url,
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // v0.7.9e — DELETE 必须用 mithril 原始 request, 不能用 app.request
  //
  // ⚠️ 实测 (as Ziven 登录态, 假 id 88888, CDP 抓包对比):
  //     app.request({method:'DELETE', url}) → 线缆上是 **POST** /lessons/88888  ❌
  //     window.m.request({method:'DELETE'}) → 线缆上是 **DELETE** /lessons/88888 ✅
  //   Flarum 2.0 的 app.request 对无 body 请求做了方法改写, 而后端 extend.php 注册的
  //   是纯 ->delete(...) 路由, 收到 POST 匹配不上 → 扩展里 7 个删除端点 (删校区 / 删教室 /
  //   删卡种 / 删约课 / 删学员卡 / 删销售跟进 / 删时间排版) 全部走在这条坏路径上。
  //   本轮修约课菜单时才发现, 属跨功能的老 bug, 一次修在公共 helper 里。
  //
  //   mithril 不是本扩展的 npm 依赖, Flarum bundle 把它挂在 window.m 上 (扩展内其它地方
  //   如 LessonOptions.js 也是这么用的), 所以这里走 window.m + 能力检测 + app.request 兜底。
  static delete(resource, id) {
    const url = app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource + '/' + id;
    const mithril = typeof window !== 'undefined' ? window.m : null;
    if (mithril && typeof mithril.request === 'function') {
      return mithril.request({
        method: 'DELETE',
        url,
        // 绕过 app.request 就得自己带 CSRF (app.request 本来会自动加)
        headers: {
          'X-CSRF-Token': app.session.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
    }
    return app.request({
      method: 'DELETE',
      url
    });
  }

  /**
   * v0.17.0 — 提交体验课 (POST /lessons/trial)
   *
   * ⚠️ **不能**用 Api.create('lessons/trial', ...) 糊弄过去: 那样 URL 会拼成
   *   `.../lessons/trial` 但走的是通用 create 的 body 包装 —— 其实能用,
   *   只是语义上容易被后人「简化」成 Api.create('lessons', ...) 而丢掉 /trial 段。
   *   单独一个方法把这条路径钉死, 改动点在 controller 侧时一眼能搜到调用方。
   */
  static createTrialLesson(attributes) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/lessons/trial',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /** v0.17.0 — 某条跟进名下的全部体验预约 (「体验详情」弹层) */
  static listTrialLessons(followupId) {
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/sales-followups/' + followupId + '/trials'
    });
  }

  /**
   * v0.18.69 (辉哥 10/23 拍板) — 「客户转学员」: 已成交跟进 ⇒ 建学员 + 办卡
   *
   *   POST /api/ziiven/scheduler/sales-followups/{id}/convert
   *
   * ⚠️ **URL 里的 {id} 是跟进记录 id**, 段数 4 段 ⇒ 后端路由必须注册在
   *   `/sales-followups/{id}` (3 段) **之前** (extend.php 里的铁律)。
   *
   * ⚠️ **前端不许传 `agents[]`** —— `agents` 由服务端自己组装 (brief §3.1)。
   *   本方法只负责把 attributes 原样发过去, ⛔ 不在这里补 agents。
   */
  static convertStudent(followupId, attributes) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/sales-followups/' + followupId + '/convert',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * v0.18.85 — 「登录名能不能用」查询 (只读, 辉哥 10/25 01:1x)
   *
   *   GET /api/ziiven/scheduler/students/check-username?username=xxx
   *
   * ⚠️ 响应是**顶层裸对象** (不是 {data:…} 信封) ⇒ **不适用** `Api.list()` 那套
   *   `.then(r => r || [])` 的信封假设 (同 Api.getCurrentSchedule 的说明)。
   *   形状: `{ available: bool, reason: 'ok'|'taken'|'format' }`
   *   ⛔ 后端**只**回这两个字段 (⛔ 不回 nickname / email / id —— 理由见后端类注释
   *   「防枚举」那一段: 这本质是个「用户名是否存在」的探测端点)。
   *
   * ⚠️ v0.19.30 — 新增**可选**第二参 `opts` (**向后兼容的纯增量**):
   *   调用方可传 `opts.errorHandler` 来**抑制 Flarum 默认的全局红条**;
   *   不传时行为与改前**逐字相同**。
   */
  static checkUsername(username, opts) {
    if (opts === void 0) {
      opts = {};
    }
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/students/check-username',
      params: {
        username: username
      },
      // v0.19.30 — 可选 `opts` (向后兼容的纯增量): 调用方可传 `opts.errorHandler`
      //   来**抑制 Flarum 默认的全局红条** (查重失败时静默放行, 由调用方就地提示)。
      //   ⚠️ `...opts` 放**最后** ⇒ 调用方**可以**覆盖 url / body / serialize / params。
      //   ⚠️ ⇒ 约定：**只允许传 `errorHandler`** (本仓唯一用途); 将来若有别的键,
      //   ⚠️    建议改成**显式白名单取值** (把 errorHandler 单独拎出来传), 别让 `...opts` 裸奔。
      ...opts
    });
  }
  static extend(resource, id, attributes) {
    return app.request({
      method: 'PATCH',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource + '/' + id + '/extend',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // v0.7.6 — 学员卡开卡 (辉哥 10/2 23:06 拍板, 未激活卡 → 一次性开卡 + 设过期时间)
  static activate(resource, id, attributes) {
    return app.request({
      method: 'PATCH',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource + '/' + id + '/activate',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // ─── v0.18.62 — 退卡 / 退费 (设计稿 v4) ───────────────────────────────
  //
  // ⚠️ **这 4 个方法必须单独写, 不能用 Api.create('student-cards/{id}/refund', …) 糊弄** ——
  //   那样 URL 会拼成 `.../student-cards/123/refund` 但语义上很容易被后人「简化」成
  //   `Api.create('student-cards', …)` 而丢掉 `/refund` 段 (同 Api.createTrialLesson 的理由)。
  //
  // ⚠️ **body 形态**: Flarum 2.0 的 `app.request` 会把 body **二次包装**
  //   (直接传 string body 会被 JSON.stringify 再包一层 ⇒ double-encoded)
  //   ⇒ 必须 `{ data: { attributes } }` + `serialize: (x) => JSON.stringify(x)`
  //     + `Content-Type: application/json` (同 Api.create)。
  //
  // ⚠️ **退款金额发纯数字**: ⛔ 不发带千分位的字符串 ('1,234.50' 不是数字 ⇒ 后端 422
  //   「退款金额必须是数字」)。带千分位只是**展示**格式 (见 RefundFormat.formatAmount)。

  /**
   * 退费预览 (只读 GET) —— v0.18.62
   *
   *   GET /api/ziiven/scheduler/student-cards/{id}/refund/preview?reason=<institution|consumer|mutual>
   *
   * ⚠️ 响应是**顶层裸对象** (不是 {data:…} 信封) ⇒ **不适用** `Api.list()` 那套
   *   `.then(r => r || [])` 的信封假设 (同 Api.getCurrentSchedule 的说明)。
   */
  static refundPreview(cardId, reason) {
    const params = {};
    if (reason) params.reason = reason;
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/student-cards/' + cardId + '/refund/preview',
      params
    });
  }

  /**
   * 「退费管理」tab 列表 (只读 GET) —— v0.18.80
   *
   *   GET /api/ziiven/scheduler/refunds
   *     ?status=&reason=&school_id=&date_from=&date_to=&q=&page=&per_page=
   *
   * ⚠️ 响应是**顶层裸对象** (不是 {data:…} 信封) ⇒ 同 refundPreview 的说明:
   *   { summary{pending_count, month_refund_amount, month_request_count, month},
   *     can_approve, pending[], records{items,total,page,per_page,pages} }
   *
   * ⚠️ **不能用 `Api.list('refunds', params)` 糊弄** —— 那套的 `.then(r => r || [])`
   *   会把裸对象当数组处理 (同 Api.createTrialLesson 的理由: URL 段不能被后人「简化」)。
   */
  static refunds(params) {
    if (params === void 0) {
      params = {};
    }
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/refunds',
      params
    });
  }

  /** 发起退费 (POST /student-cards/{id}/refund) —— attributes: {refund_reason, refund_amount, refund_note} */
  static requestRefund(cardId, attributes) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/student-cards/' + cardId + '/refund',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /** 审批通过 (POST /student-cards/{id}/refund/approve) —— attributes: {approver_note} */
  static approveRefund(cardId, attributes) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/student-cards/' + cardId + '/refund/approve',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /** 驳回 (POST /student-cards/{id}/refund/reject) —— attributes: {approver_note} */
  static rejectRefund(cardId, attributes) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/student-cards/' + cardId + '/refund/reject',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * v0.18.45 (辉哥 10/21 拍板) — 「仪表盘」聚合端点
   *
   *   GET /api/ziiven/scheduler/dashboard/overview?school_id=&range=&start=&end=
   *
   * ## 为什么**必须**是一个聚合端点 (而不是前端并发打几个既有端点)
   *   辉哥的红线: 敏感字段在响应里**根本不存在** (不是 null、不是 0、更不是前端遮挡)。
   *   前端并发 ⇒ 每个端点各自返一份完整数据 ⇒ 前端「不显示」时数据**已经出网关了**。
   *   ⇒ 响应形状必须由**服务端**按角色裁剪 (后端 Dashboard\OverviewController)。
   *
   * ## 为什么同时传 `range` 与 `start`/`end`
   *   `lessons.start_at` 存的是**用户本地时间字面量**, 而服务器 `date()` 是 UTC ⇒
   *   服务端自己算「今天」会差 8 小时。⇒ 区间由**浏览器本地时区**算好传过来,
   *   后端只做字面量比较 (`range` 只是给服务端做展示/兜底用)。
   *   口径与后端 OverviewController::resolveRange 及 ListLessonsPageController (v0.18.33) 一致。
   */
  static dashboardOverview(params) {
    if (params === void 0) {
      params = {};
    }
    const clean = {};
    Object.keys(params).forEach(k => {
      const v = params[k];
      if (v !== undefined && v !== null && v !== '') clean[k] = v;
    });
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/dashboard/overview',
      params: clean
    });
  }

  // ─── Reorder (v0.5.0p 第二阶段) ─────────────────────────────
  // 拖拽结束批量更新 sort_order — 1 次 POST, ids 是新顺序的 id 数组
  static reorder(resource, ids, params) {
    if (params === void 0) {
      params = {};
    }
    let url = app.forum.attribute('apiUrl') + '/ziiven/scheduler/' + resource + '/reorder';
    const qs = Object.entries(params).filter(_ref => {
      let [, v] = _ref;
      return v !== undefined && v !== null && v !== '';
    }).map(_ref2 => {
      let [k, v] = _ref2;
      return encodeURIComponent(k) + '=' + encodeURIComponent(v);
    }).join('&');
    if (qs) url += '?' + qs;
    return app.request({
      method: 'POST',
      url,
      body: {
        data: {
          attributes: {
            ids
          }
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // ─── 校区课表 (v0.17.54 后端 / v0.17.57 前端读侧) ──────────────────
  //
  // ⚠️ v0.17.57 — 下面这 5 个 `time-schedule*` SCD 方法连同它们的后端路由一起下线了,
  //   整段删除。当时的残留调用方 (TimeScheduleSection / NewVersionModal) 到 **v0.17.58** 才真正删掉,
  //   本文件里对它们的描述已随那次删除失效, 别再当现状读。两个组件的**拖拽排序范式**被
  //   SchoolScheduleModal 原样继承 (rows 列表 Sortable.create + onEnd splice 重排)。
  //
  // v0.17.57 新增: 课表日历与约课弹窗都改读这一个端点。
  //   三种响应都是 HTTP 200 —— 「没配课表」是正常状态, 不是错误:
  //     ① 配了且当前生效 → { school_id, today, reason: null, data: { …版本…, nodes: [...] } }
  //     ② 一条版本都没有   → { school_id, today, reason: 'not_configured', data: null }
  //     ③ 有版本但都过期   → { school_id, today, reason: 'stale', data: null, versions: [...] }
  //   调用方只需判 `reason === null` 才画课表, 否则走「真实时间刻度 + 老节拍」兜底渲染。
  //
  // v0.17.85 新增: **可选** `params.date` ('YYYY-MM-DD') —— 问「那一天用的是哪一版」,
  //   而不只是「今天」。辉哥 10/17: A 版 10-01 起, 10-17 建了 B 版, A 与 B 之间约了很多课,
  //   看日历 10-16 必须显示 A 的时段, 10-17 才显示 B 的。
  //   ⚠️ **不传 params / 不传 date = 后端按今天判**, 行为与 v0.17.54 逐字一致 ——
  //     SchoolsTab.js:66 与 LessonEditorModal.js:937 两个调用点因此一个字都不用改。
  //   ⚠️ date 非法后端回 **400** (不静默退回今天), 所以这里传值前必须是调用方自己的
  //     localDateStr() 结果 (它只会产 'YYYY-MM-DD' 或 '')。
  //
  // ⚠️ 响应**不是** { data: ... } 信封: 顶层直接就是上面那个对象, data 只是其中一个键
  //   (内层才是版本 + nodes)。别用 Api.list() 那套 .then(r => r) 的信封假设去解它。
  static async getCurrentSchedule(schoolId, params, opts) {
    if (opts === void 0) {
      opts = {};
    }
    let url = app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + schoolId + '/schedule';
    // 空串按「没指定」处理: 直接不发这个 query, 免得后端把 '?date=' 判成非法。
    if (params && params.date) {
      url += '?date=' + encodeURIComponent(params.date);
    }
    const r = await app.request({
      method: 'GET',
      url: url,
      // v0.19.00 — 可选的静默错误处理器 (见 Api.list 的注释); 不传 = 行为不变。
      errorHandler: opts.errorHandler
    });
    return r || null;
  }

  // v0.17.58 — 课表配置「写侧」4 个方法。
  //
  // ⚠️ **PUT / DELETE 必须走 window.m.request, 不能走 app.request** ——
  //   理由同 Api.update / Api.delete L43-48、L71-98 的实测记录: Flarum 2.0 的 app.request
  //   会把无 body 的 PATCH/DELETE 改写成 POST, 而后端注册的是纯 ->put(...) / ->delete(...)，
  //   改写后匹配不上直接 405。这里统一走 mithril 原始 request + 手工带 CSRF。
  //
  // Body 形态: 后端 `CreateScheduleVersionController` L60 是
  //   `$attrs = $body['data']['attributes'] ?? $body` —— 两种都收, 这里跟项目惯例用前者。
  //
  // `{vid}` = 该版本的 effective_from (YYYY-MM-DD), 沿用后端约定的 URL-safe 业务主键,
  //   **不是**自增 id (命名沿用老 time-schedule-versions 端点, 见 extend.php L308)。

  /**
   * GET 版本列表 (含历史)。
   *
   * 响应是 `{school_id, today, versions, overlapping_current_count, warning?}` 信封, **不是裸数组**
   * (与日历端点的形态不同, 别混)。这里整包返回, 方便调用方直接取 `warning`:
   *   同一校区命中多条当前版本时后端会在 `warning` 里显式说明「fc 与约课校验会因此报 500」,
   *   那是排查数据异常的入口, 不该被前端吞掉。
   */
  static async listScheduleVersions(schoolId) {
    const r = await app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + schoolId + '/schedule-versions'
    });
    return {
      versions: r && r.versions || [],
      warning: r && r.warning || '',
      overlappingCurrentCount: r && r.overlapping_current_count || 0
    };
  }

  /** POST 新建版本 —— 版本头 + 全部节点在后端一个事务里写入 */
  static createScheduleVersion(schoolId, attributes) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + schoolId + '/schedule-versions',
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * PUT 编辑版本 —— **节点是全量替换** (后端 delete + 重建, 见 UpdateScheduleVersionController L82-92)
   *
   * ⚠️ effective_from 由 URL 里的 vid 决定, 后端 L75-77 **根本不读 body 里的 effective_from**
   *   (`$version->effective_from` 那行不存在), 所以编辑态下这个字段前端要置灰, 否则用户改了会以为生效。
   */
  static updateScheduleVersion(schoolId, vid, attributes) {
    const url = app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + schoolId + '/schedule-versions/' + vid;
    const mithril = typeof window !== 'undefined' ? window.m : null;
    if (mithril && typeof mithril.request === 'function') {
      return mithril.request({
        method: 'PUT',
        url,
        body: {
          data: {
            attributes
          }
        },
        serialize: x => JSON.stringify(x),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': app.session.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
    }
    return app.request({
      method: 'PUT',
      url,
      body: {
        data: {
          attributes
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /** DELETE 删版本。当前后端有守卫: 当前生效版本不可删 (会 422), 前端已提前禁用该按钮。 */
  static deleteScheduleVersion(schoolId, vid) {
    const url = app.forum.attribute('apiUrl') + '/ziiven/scheduler/schools/' + schoolId + '/schedule-versions/' + vid;
    const mithril = typeof window !== 'undefined' ? window.m : null;
    if (mithril && typeof mithril.request === 'function') {
      return mithril.request({
        method: 'DELETE',
        url,
        headers: {
          'X-CSRF-Token': app.session.csrfToken,
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
    }
    return app.request({
      method: 'DELETE',
      url
    });
  }

  // v0.7.8h — actor role (辉哥 10/3 22:20 拍板, 学员只看到签到/取消按钮)
  static async getActorRole() {
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/actor-role'
    });
  }

  // ─── v0.9.0 固定课 (recurring) ───────────────────────────────
  // 多选星期的实现 (决策 B3): UI 多选, 底层拆成 N 行模板 —— 前端并发发 N 个 POST。
  // 后端刻意不收数组: 模板行 = 一个 (学员, 星期几, 时间) 组合, 一行一条规则。
  // 拆开的代价是这里要循环, 好处是改某一天不必整体 PATCH 另外六天。

  static listFixedLessons(params, opts) {
    if (params === void 0) {
      params = {};
    }
    if (opts === void 0) {
      opts = {};
    }
    return Api.list('fixed-lessons', params, opts);
  }

  /**
   * 建固定课。attrs.day_of_week 是**单个**数字。
   * 多选请调 createFixedLessons (下面那个)。
   */
  static createFixedLesson(attrs) {
    return Api.create('fixed-lessons', attrs);
  }

  /** 多选星期 → 并发建 N 行模板。任一行失败整个 reject (让调用方弹错误提示)。 */
  static createFixedLessons(attrs, daysOfWeek) {
    const days = (daysOfWeek || []).filter(d => d !== null && d !== undefined && d !== '');
    if (!days.length) return Promise.reject(new Error('请至少选择一个星期'));
    return Promise.all(days.map(d => Api.create('fixed-lessons', Object.assign({}, attrs, {
      day_of_week: d
    }))));
  }
  static updateFixedLesson(id, attrs) {
    return Api.update('fixed-lessons', id, attrs);
  }
  static deleteFixedLesson(id) {
    return Api.delete('fixed-lessons', id);
  }

  /**
   * 固定课物化窗口预测 (只读 GET) —— v0.18.77
   *   GET /api/ziiven/scheduler/fixed-lessons/forecast?school_id=N&dow=3[&room_id=&schedule_node_id=&start_time=]
   *
   * ⚠️ 响应是**顶层裸对象** (不是 {data:…} 信封) ⇒ 与 Api.refundPreview 同款说明。
   * ⚠️ `school_id` / `dow` 是**必填**, 其余三个**可选** (选了教室/时段才判冲突)。
   * ⚠️ `count` (共几次) 由**后端**下发, 随时间滚动 —— ⛔ 前端不许写死。
   */
  static forecastFixedLessons(schoolId, dow, opts) {
    if (opts === void 0) {
      opts = {};
    }
    const params = {
      school_id: schoolId,
      dow: dow
    };
    if (opts.room_id) params.room_id = opts.room_id;
    if (opts.schedule_node_id) params.schedule_node_id = opts.schedule_node_id;
    if (opts.start_time) params.start_time = opts.start_time;
    return app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/fixed-lessons/forecast',
      params
    });
  }

  /** 检查排课 (扫本月+下月) */
  static checkFixedLessons(schoolId) {
    return Api.get('fixed-lessons/check', {
      school_id: schoolId
    });
  }

  /** 一键修复: action = 'backfill' (补排漏排) | 'resume' (恢复因卡过期停排) */
  static fixFixedLessons(schoolId, action) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/fixed-lessons/fix',
      body: {
        data: {
          attributes: {
            school_id: schoolId,
            action
          }
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  /**
   * 固定课模式拖块 = 改整个系列 (决策 A3 的另一半)
   * 走后端专用端点而不是 lessons/{id} PATCH: 后端要在**一个事务里**
   * 同时改模板 + 级联未来课次, 少了模板这一步, 下次物化会冒出旧时间的课次。
   */
  static moveFixedSeries(attrs) {
    return app.request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/fixed-lessons/series/move',
      body: {
        data: {
          attributes: attrs
        }
      },
      serialize: x => JSON.stringify(x),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  // ─── v0.19.15 「学员自主约课」三个端点 (辉哥 10/27 拍板) ─────────────────────
  //
  // ⚠️ **三个都必须单写一个方法** (brief F29) ——
  //   ⛔ 不许在调用点直接写 `Api.create('lessons/self-booking', …)`:
  //   那样的写法很容易被后人「简化」成 `Api.create('lessons', …)` 而**丢掉路径段**,
  //   而这条路径段正是「学员端专用入口」的全部意义 (员工端 POST /lessons 学员过不了那道门)。
  //   同 Api.createTrialLesson / 退费那四个方法的同一条理由。
  //
  // ⚠️ 三个都是 POST/GET, **不需要** window.m.request:
  //   Flarum 2.0 只把**无 body** 的 PATCH/DELETE 改写成 POST (Api.update / Api.delete 的注释),
  //   带 body 的 POST 走 app.request 没问题 (见 Api.create)。

  /**
   * 可约时段列表 (只读 GET)
   *
   *   GET /api/ziiven/scheduler/lessons/self-booking-slots?school_id=&date=
   *
   * ⚠️ 响应是**顶层裸对象** (不是 {data:…} 信封) ⇒ **不适用** `Api.list()` 那套
   *   `.then(r => r || [])` 的信封假设 (同 Api.refundPreview / Api.getCurrentSchedule 的说明)。
   *   形状: { school_id, date, window:{from,to}, slots:[…], coaches:[…] }
   */
  static listSelfBookingSlots(params) {
    return Api.get('lessons/self-booking-slots', params);
  }

  /**
   * 学员自主约课 (POST /lessons/self-booking)
   *
   *   attributes: { student_card_id, schedule_node_id, coach_id, start_at, end_at }
   *   ⚠️ `coach_id` 可为 null (不指定 ⇒ 由校区安排); `start_at`/`end_at` 是
   *      **本地墙上时刻字面量** ('YYYY-MM-DD HH:MM:SS'), 与员工端 payload 同形。
   */
  static selfBookLesson(attributes) {
    return Api.create('lessons/self-booking', attributes);
  }

  /** 取消**自己约的**那一节课 (POST /lessons/{id}/self-cancel) */
  static cancelSelfBookedLesson(id) {
    return Api.create('lessons/' + id + '/self-cancel', {});
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/utils/Api', Api);